import React from 'react';
import { logger } from 'src/utils';

export type AsyncActionCallback<TArgs extends unknown[] = []> = (
  ...args: TArgs
) => void | Promise<void>;

export interface IAsyncAction<TArgs extends unknown[] = []> {
  loading: boolean;
  /** Runs the action, awaiting it if it returns a promise. Resolves true when it completed, false when it threw — never rejects, so callers can gate UI transitions on the result. */
  run: (...args: TArgs) => Promise<boolean>;
}

/** @registry Runs a caller-supplied action that may be sync or async: tracks a loading flag, always clears it in a finally, and reports success/failure as a boolean so a component only advances its UI (close the modal, leave edit mode, leave confirm mode) when the action actually succeeded. Logs failures via logger.error; it deliberately does NOT toast — callers own user-facing messaging and already do it. */
export const useAsyncAction = <TArgs extends unknown[] = []>(
  action: AsyncActionCallback<TArgs>,
): IAsyncAction<TArgs> => {
  const [loading, setLoading] = React.useState(false);

  const actionRef = React.useRef(action);
  actionRef.current = action;

  // Guards re-entrancy with a ref rather than state: `run` reads this
  // synchronously before the loading-state update would have flushed.
  const inFlightRef = React.useRef(false);

  const run = React.useCallback(async (...args: TArgs): Promise<boolean> => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setLoading(true);
    try {
      await actionRef.current(...args);
      return true;
    } catch (err) {
      logger.error('Async action failed', { err });
      return false;
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  return { loading, run };
};
